/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */
import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Button, Loader } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useAPIData } from "~/hooks/useAPIData";
import {
  CustomerRequest,
  RequestCategory,
  RouteParams,
  Request,
} from "~/models";
import { generateActionSuccessText } from "~/tools";
import { useAxios } from "~/tools/hooks/useAxios";
import RequestField from "./RequestField";

const RadioWrapper = styled.div`
  input + div {
    z-index: 0;
  }
`;

type Props = {
  serviceId: string;
  assignedRequests: CustomerRequest[];
  refetch: () => Promise<void>;
};

export default function ({ serviceId, assignedRequests, refetch }: Props) {
  const state = useParams<RouteParams>();
  const { customerId } = state;

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const methods = useForm();
  const watcher = methods.watch();

  const [reqCategories, optionsLoading] = useAPIData<RequestCategory[]>(
    `/services/${serviceId}`,
    (data) => data.requestCategories,
    []
  );

  React.useEffect(() => {
    if (watcher.requestId) {
      const category = reqCategories.find((rc) =>
        rc.requests.some((r) => r.id === watcher.requestId)
      );
      methods.setValue("category", category?.id);
    }
  }, [watcher.requestId]);

  const categoryOptions = reqCategories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const assignedRequestIds = assignedRequests.map((req) => req.id);
  const requests: Request[] = [];
  reqCategories
    .filter((rc) => !watcher.category || watcher.category === rc.id)
    .forEach((rc) => {
      requests.push(
        ...rc.requests.filter((r) => !assignedRequestIds.includes(r.id))
      );
    });

  const requestOptions = requests.map((r) => ({ label: r.name, value: r.id }));

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      await Axios().post(`/customers/${customerId}/requests`, data);
      showSuccess(generateActionSuccessText(`Request`)()(`assigned`)());
      refetch();
    } catch (e) {
      showError(e.response);
    }
    setIsLoading(false);
    setIsSidebarOpen(false);
  };

  const request = requests.find((r) => r.id === watcher.requestId);

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "8px" }}
        size="sm"
        severity="low"
        icon="link"
      >
        Assign request
      </Button>

      <SidebarForm
        title="Assign Request"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        {optionsLoading ? (
          <div style={{ marginBottom: "1.5rem" }}>
            <Loader inContent />
          </div>
        ) : (
          <>
            <SidebarDropdown
              name="category"
              options={categoryOptions}
              label="Request category"
            />

            <SidebarDropdown
              name="requestId"
              options={requestOptions}
              label="Request"
              required
            />

            <RadioWrapper>
              <RequestField
                label="Included in base fee"
                field="includedInBaseFee"
                request={request}
              />
              <RequestField
                label="Extra chargeable"
                field="extraChargeable"
                request={request}
              />
              <RequestField
                label="Valid in IaaS"
                field="validInIaaS"
                request={request}
              />
              <RequestField
                label="Valid in PaaS"
                field="validInPaaS"
                request={request}
              />
            </RadioWrapper>
          </>
        )}

        <SidebarFormButtons
          onCancel={() => setIsSidebarOpen(false)}
          submitText="Assign"
          submitLoading={isLoading}
          submitEnabled={!!watcher.requestId}
        />
      </SidebarForm>
    </>
  );
}
